import { MenuIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Button } from "../components/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/sheet";
import { cn } from "../lib/utils";
import { BottomTabs } from "./bottom-tabs";
import { LeftRail, NavList } from "./left-rail";
import type { NavItem, NavLinkComponent } from "./nav";

type AppShellProps = {
	items: NavItem[];
	activeKey: string | undefined;
	navLabel: string;
	brand?: ReactNode;
	/** Account menu, shown at the foot of the rail and in the phone top bar. */
	account?: ReactNode;
	linkComponent?: NavLinkComponent;
	variant?: "rail" | "sidebar";
	/**
	 * Under 768 px: bottom tabs (staff, five sections at most) or a menu button
	 * in the top bar that opens the navigation in a sheet (admin).
	 */
	mobileNav?: "tabs" | "sheet";
	/** Accessible name of the menu button when `mobileNav` is "sheet". */
	menuLabel?: string;
	children: ReactNode;
};

/**
 * The frame of staff web and admin: a left rail on wide screens, bottom tabs
 * under 768 px. Navigation never moves between screens.
 */
export function AppShell({
	items,
	activeKey,
	navLabel,
	brand,
	account,
	linkComponent,
	variant = "rail",
	mobileNav = "tabs",
	menuLabel = "Menu",
	children,
}: AppShellProps) {
	const tabs = mobileNav === "tabs";
	const [menuOpen, setMenuOpen] = useState(false);
	return (
		<div data-slot="app-shell" className="flex min-h-dvh bg-background text-foreground">
			<LeftRail
				items={items}
				activeKey={activeKey}
				label={navLabel}
				header={brand}
				footer={account}
				linkComponent={linkComponent}
				variant={variant}
			/>
			<div className="flex min-w-0 flex-1 flex-col">
				<div className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-background px-2 md:hidden">
					{tabs ? null : (
						<Sheet open={menuOpen} onOpenChange={setMenuOpen}>
							<SheetTrigger render={<Button variant="ghost" size="icon" aria-label={menuLabel} />}>
								<MenuIcon />
							</SheetTrigger>
							<SheetContent side="left" className="w-72 p-2">
								<SheetHeader className="p-2">
									<SheetTitle>{brand}</SheetTitle>
								</SheetHeader>
								{/* Navigating closes the sheet. */}
								<nav aria-label={navLabel} onClickCapture={() => setMenuOpen(false)}>
									<NavList items={items} activeKey={activeKey} linkComponent={linkComponent} />
								</nav>
							</SheetContent>
						</Sheet>
					)}
					<div className={cn("flex-1", tabs && "pl-2")}>{brand}</div>
					{account}
				</div>
				<main className={cn("flex-1 px-4 py-4 md:px-8 md:py-6", tabs && "pb-24 md:pb-6")}>
					{children}
				</main>
			</div>
			{tabs ? (
				<BottomTabs
					items={items}
					activeKey={activeKey}
					label={navLabel}
					linkComponent={linkComponent}
				/>
			) : null}
		</div>
	);
}
